import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useCreateListingDialog } from "@/contexts/create-listing-dialog-provider";
import { useListings } from "@/contexts/listings-provider";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { CompanyDTO, ListingDTO } from "@shaarp/common";
import { ListChecks, ListIcon } from "lucide-react";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useState } from "react";
import CreateListingForm from "../listing/create-listing-form";
import { Button } from "../ui/button";
import { toast, useToastError } from "../ui/use-toast";
import CompanyListingsSelectorItem from "./company-listings-selector-item";

type MobileSheetView = "select" | "create";

type CompanyListingsSelectorProps = {
  readonly company: CompanyDTO;
  readonly compact?: boolean;
  readonly triggerClassName?: string;
  readonly onActionComplete?: () => void;
  /** Opens the list picker as it mounts: the click that asked for it saved the company first. */
  readonly defaultOpen?: boolean;
};

export default function CompanyListingsSelector({
  company,
  compact = false,
  triggerClassName,
  onActionComplete,
  defaultOpen = false,
}: CompanyListingsSelectorProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const { userListings, shaarpListings, apiFetchListings } = useListings();
  const { openCreateListingDialog } = useCreateListingDialog();

  const [open, setOpen] = useState<boolean>(defaultOpen);
  const [mobileView, setMobileView] = useState<MobileSheetView>("select");

  const [listingsIds, setListingsIds] = useState<Set<string>>(
    () => new Set(company.listingsIds ?? [])
  );

  useEffect(() => {
    setListingsIds(new Set(company.listingsIds ?? []));
  }, [company.listingsIds]);

  useEffect(() => {
    const init = async () => {
      await apiFetchListings();
    };
    init();
  }, []);

  const companyUserListings = userListings.filter((l) => l.listingType === "companies");
  const companyShaarpListings = shaarpListings.filter((l) => l.listingType === "companies");

  const availableListings = [...companyUserListings, ...companyShaarpListings];

  const selectListing = async (listing: ListingDTO) => {
    if (listingsIds.has(listing.id)) {
      await removeFromListing(listing);
    } else {
      await addToListing(listing);
    }
    await apiFetchListings({ force: true });
    setOpen(false);
    onActionComplete?.();
  };

  const addToListing = async (listing: ListingDTO) => {
    setListingsIds((prev) => {
      const next = new Set(prev);
      next.add(listing.id);
      return next;
    });
    try {
      await api.post(`/listing/${listing.id}/companies`, {
        companiesIds: [company.id],
      });
      toast({
        variant: "default",
        title: t("company.listings.addedToList", {
          listingTitle: listing.title,
        }),
        description: t("company.listings.addedToListDescription", {
          companyName: company.name,
        }),
      });
    } catch (error) {
      setListingsIds((prev) => {
        const next = new Set(prev);
        next.delete(listing.id);
        return next;
      });
      toastError();
    }
  };

  const removeFromListing = async (listing: ListingDTO) => {
    setListingsIds((prev) => {
      const next = new Set(prev);
      next.delete(listing.id);
      return next;
    });
    try {
      await api.delete(`/listing/${listing.id}/companies`, {
        data: { companiesIds: [company.id] },
      });
      toast({
        variant: "default",
        title: t("company.listings.removedFromList", {
          listingTitle: listing.title,
        }),
        description: t("company.listings.removedFromListDescription", {
          companyName: company.name,
        }),
      });
    } catch (error) {
      setListingsIds((prev) => {
        const next = new Set(prev);
        next.add(listing.id);
        return next;
      });
      toastError();
    }
  };

  /** The company travelled with the creation request; only local state is left. */
  const onListingCreatedWithCompany = async (listing: ListingDTO) => {
    setListingsIds((prev) => new Set(prev).add(listing.id));
    await apiFetchListings({ force: true });
    setOpen(false);
    onActionComplete?.();
    toast({
      variant: "default",
      title: t("company.listings.addedToList", { listingTitle: listing.title }),
      description: t("company.listings.addedToListDescription", {
        companyName: company.name,
      }),
    });
  };

  const onMobileListingCreated = (newListing: ListingDTO) => {
    setMobileView("select");
    onListingCreatedWithCompany(newListing);
  };

  const sheetTitleLabel = t(
    "company.listings.button" as Parameters<typeof t>[0],
  );

  const renderTrigger = (onClick?: () => void) => {
    const addedListings = availableListings.filter((l) =>
      listingsIds.has(l.id),
    );
    const isAdded = addedListings.length > 0;
    const label =
      addedListings.length === 1
        ? t("company.listings.addedTo", { name: addedListings[0].title })
        : addedListings.length > 1
          ? t("company.listings.addedToCount", { count: addedListings.length })
          : sheetTitleLabel;

    return (
      <Button
        variant="outline"
        className={cn(
          "relative h-10 gap-2",
          compact ? "w-10" : "px-3",
          isAdded && !triggerClassName && "border-primary text-primary",
          triggerClassName,
        )}
        role="combobox"
        aria-expanded={open}
        onClick={onClick}
      >
        {isAdded ? (
          <ListChecks className="w-4 h-4 shrink-0" />
        ) : (
          <ListIcon className="w-4 h-4 shrink-0" />
        )}
        {!compact && <span className="text-sm truncate">{label}</span>}
      </Button>
    );
  };

  const handleNewListClick = () => {
    if (isMobile) {
      setMobileView("create");
      return;
    }
    setOpen(false);
    openCreateListingDialog({
      defaultListingType: "companies",
      companiesIds: [company.id],
      onCreated: onListingCreatedWithCompany,
    });
  };

  const handleMobileOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setMobileView("select");
    }
  };

  const commandContent = (
    <Command>
      {availableListings.length > 0 ? (
        <>
          <CommandInput
            placeholder={t("common.search")}
            classNameWrapper={isMobile ? "px-4" : undefined}
            className={isMobile ? "h-12 text-base" : undefined}
          />
          <CommandList className={isMobile ? "max-h-none flex-1" : undefined}>
            <CommandEmpty>{t("common.noResults")}</CommandEmpty>
            {companyUserListings.length > 0 && (
              <CommandGroup heading={t("company.listings.personalLists")}>
                {companyUserListings.map((listing) => (
                  <CompanyListingsSelectorItem
                    key={listing.id}
                    isSelected={listingsIds.has(listing.id)}
                    listing={listing}
                    selectListing={selectListing}
                  />
                ))}
              </CommandGroup>
            )}
            {companyShaarpListings.length > 0 && (
              <CommandGroup heading={t("company.listings.shaarpLists")}>
                {companyShaarpListings.map((listing) => (
                  <CompanyListingsSelectorItem
                    key={listing.id}
                    isSelected={listingsIds.has(listing.id)}
                    listing={listing}
                    selectListing={selectListing}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </>
      ) : (
        <CommandGroup heading={t("company.listings.personalLists")}>
          <CommandItem disabled={true}>
            {t("company.listings.noLists")}
          </CommandItem>
        </CommandGroup>
      )}
      <CommandSeparator />
      <CommandList>
        <CommandGroup>
          <CommandItem
            onSelect={handleNewListClick}
            className={isMobile ? "py-3 px-3 text-[15px]" : undefined}
          >
            <PlusCircledIcon
              className={cn("mr-2", isMobile ? "w-5 h-5" : "w-5 h-5")}
            />
            {t("company.listings.newList")}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <>
        {renderTrigger(() => setOpen(true))}
        <MobileSheet
          open={open}
          onOpenChange={handleMobileOpenChange}
          heightClass="h-[80dvh]"
        >
          <div className="px-5 pt-2 pb-3 shrink-0 flex items-center gap-2">
            {mobileView === "create" && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2 shrink-0"
                onClick={() => setMobileView("select")}
                aria-label={t("common.back")}
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Button>
            )}
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {mobileView === "create"
                ? t("listing.dialog.create.title")
                : sheetTitleLabel}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {mobileView === "create"
                ? t("listing.dialog.create.title")
                : sheetTitleLabel}
            </SheetDescription>
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {mobileView === "create" ? (
              <CreateListingForm
                variant="sheet"
                defaultListingType="companies"
                companiesIds={[company.id]}
                onCancel={() => setMobileView("select")}
                onCreated={onMobileListingCreated}
              />
            ) : (
              commandContent
            )}
          </div>
        </MobileSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{sheetTitleLabel}</TooltipContent>
      </Tooltip>
      <PopoverContent className="p-0" side="top" align="center">
        {commandContent}
      </PopoverContent>
    </Popover>
  );
}
